<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import type { GradeCode } from '~/models/Grade'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { colors, forceColorShade, getColorForId } from '~/utils/colors'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useContentHelper } from '~/composables/useContentHelper'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = defineProps<{
  resource: BaseItem
  isAnchor?: boolean
  isUpcoming?: boolean
  subjectCode?: SubjectCode
  selectedGrade?: GradeCode
}>()

const { t } = useI18n()
const { getTitle, isHeader } = useContentHelper()
const { buildResourceUrl, buildSubjectHeaderUrl } = useUrlBuilder()

const link = computed(() => props.isUpcoming && props.subjectCode && props.selectedGrade && isHeader(props.resource)
  ? buildSubjectHeaderUrl(props.resource, props.subjectCode, props.selectedGrade)
  : buildResourceUrl(props.resource)
)

const title = computed(() => getTitle(props.resource))
const color = computed(() => getColorForId(props.resource.locationId ?? 0, colors))
</script>

<template>
  <article class="group motion-safe:animate-fade-in-down relative space-y-3 rounded-md text-lg has-focus-visible:ring">
    <CardMenu
      :resource="resource"
      button-classes="absolute top-4 left-4 z-20"
    />
    <div class="relative aspect-square size-full overflow-hidden rounded-lg">
      <aside
        v-if="resource.new"
        class="bg-green-40 pointer-events-none absolute top-0 right-0 z-20 rounded-bl-2xl p-1.5 pl-2 text-sm font-medium tracking-wider text-white uppercase"
        v-text="t('newShort')"
      />
      <aside
        v-if="resource.coming"
        class="text-green-40 pointer-events-none absolute inset-4 z-20 grid place-content-center rounded-lg bg-white/90 text-center"
      >
        <div class="text-2xl uppercase" v-text="t('coming')" />
      </aside>
      <img
        v-if="resource.thumbnail"
        :src="resource.thumbnail"
        class="block size-full rounded-lg object-cover"
        loading="eager"
        :alt="resource.thumbnailAlt"
        aria-hidden="true"
      >
      <div
        v-else
        class="block size-full rounded-lg object-cover"
        :style="{ backgroundColor: forceColorShade(color, '20') }"
      />
    </div>
    <span class="line-clamp-3 text-center text-sm font-bold sm:text-lg">
      <LinkOrAnchor
        v-if="!resource.coming"
        :link="link"
        class="outline-0 before:absolute before:inset-0 before:z-10 hover:underline"
        :is-anchor="props.isAnchor"
      >
        {{ title }}
      </LinkOrAnchor>
      <div
        v-else
        class="cursor-not-allowed before:absolute before:inset-0 before:z-10"
        v-text="title"
      />
    </span>
  </article>
</template>
